import React, { useState } from 'react';
import { Calendar, Download, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../../backend/services/supabase';

interface ImportResult {
  imported_records: number;
  duplicate_skipped: number;
  campaigns_processed: number;
  date_range: {
    start: string;
    end: string;
    days: number;
  };
  per_campaign: Record<string, any>;
}

export const HistoricalImport: React.FC = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Set default dates (last 90 days)
  React.useEffect(() => {
    const today = new Date();
    const ninetyDaysAgo = new Date(today.getTime() - (90 * 24 * 60 * 60 * 1000));
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(ninetyDaysAgo.toISOString().split('T')[0]);
  }, []);

  const handleImport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    if (daysDiff <= 0) {
      setError('End date must be after start date');
      return;
    }

    if (daysDiff > 365) {
      setError('Date range cannot exceed 365 days');
      return;
    }

    setIsImporting(true);
    setError(null);
    setImportResult(null);

    try {
      const session = await supabase.auth.getSession();
      
      const response = await fetch('/api/meta/historical-import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session?.access_token}`
        },
        body: JSON.stringify({
          startDate,
          endDate
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Import failed');
      }

      setImportResult(result);
      toast.success(`Successfully imported ${result.imported_records} records (${result.duplicate_skipped} duplicates skipped)`);

    } catch (error: any) {
      setError(error.message);
      toast.error('Historical import failed');
    } finally {
      setIsImporting(false);
    }
  };

  const getDaysInRange = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  };

  const daysInRange = getDaysInRange();

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      <div className="flex items-center mb-4">
        <Calendar className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Historical Data Import
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Import past Meta Ads spending data (up to 1 year)
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Date Range Selection */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              max={endDate || new Date().toISOString().split('T')[0]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              min={startDate}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>

        {/* Date Range Info */}
        {daysInRange > 0 && (
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <Calendar className="h-4 w-4 mr-1" />
            {daysInRange} days selected
            {daysInRange > 365 && (
              <span className="ml-2 text-red-600 dark:text-red-400">
                (exceeds 365 day limit)
              </span>
            )}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="flex items-center p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0" />
            <span className="text-red-700 dark:text-red-300">{error}</span>
          </div>
        )}

        {/* Import Button */}
        <button
          onClick={handleImport}
          disabled={isImporting || !startDate || !endDate || daysInRange > 365 || daysInRange <= 0}
          className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isImporting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Importing... This may take a few minutes
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Import Historical Data
            </>
          )}
        </button>

        {/* Import Results */}
        {importResult && (
          <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-md">
            <div className="flex items-center mb-3">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
              <span className="font-medium text-green-800 dark:text-green-200">
                Import Completed Successfully!
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-green-700 dark:text-green-300">Records Imported:</span>
                <span className="ml-2 font-medium">{importResult.imported_records.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-green-700 dark:text-green-300">Duplicates Skipped:</span>
                <span className="ml-2 font-medium">{importResult.duplicate_skipped.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-green-700 dark:text-green-300">Campaigns Processed:</span>
                <span className="ml-2 font-medium">{importResult.campaigns_processed}</span>
              </div>
              <div>
                <span className="text-green-700 dark:text-green-300">Date Range:</span>
                <span className="ml-2 font-medium">{importResult.date_range.days} days</span>
              </div>
            </div>

            {/* Per Campaign Details */}
            {Object.keys(importResult.per_campaign).length > 0 && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-medium text-green-700 dark:text-green-300">
                  View Campaign Details
                </summary>
                <div className="mt-2 space-y-1 text-xs">
                  {Object.entries(importResult.per_campaign).map(([campaignId, result]: [string, any]) => (
                    <div key={campaignId} className="flex justify-between items-center py-1">
                      <span className="font-mono">{campaignId}</span>
                      {result.success ? (
                        <span className="text-green-600 dark:text-green-400">
                          {result.records_inserted} imported, {result.duplicates_skipped} skipped
                        </span>
                      ) : (
                        <span className="text-red-600 dark:text-red-400">
                          {result.error}: {result.message}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700 dark:text-blue-300">
              <p className="font-medium mb-1">How it works:</p>
              <ul className="space-y-1 text-xs">
                <li>• Fetches daily spend data from Meta Marketing API</li>
                <li>• Converts to hourly records with realistic distribution patterns</li>
                <li>• Automatically prevents duplicate imports</li>
                <li>• Updates budget spreadsheet with historical performance data</li>
                <li>• Safe to run multiple times - duplicates are automatically skipped</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};



